import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, notification, Tooltip, Space } from "antd";
import { FileExcelOutlined, FileTextOutlined } from "@ant-design/icons";
import { getInformeGeneralPatti } from "../../../services/visitas_tecnicas/informe/getDataVisitaService";
import { useSelector } from "react-redux";


const ExportInformeGeneral = () => {
  const [dataCompleta, setDataCompleta] = useState([]);
  const [loading, setLoading] = useState(false); // Estado de carga
  const [loadingExcel, setLoadingExcel] = useState(false); // Estado para Excel
  const [loadingJson, setLoadingJson] = useState(false); // Estado para JSON
    const usernameFromState = useSelector((state) => state.auth.user);
  
    
  const fetchDetalleVisitas = async () => {
    try {
      const response = await getInformeGeneralPatti( usernameFromState.email);
      const { data } = response;
      if (data.estado) {
        setDataCompleta(data);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to fetch detalle visitas data.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Ocurrió un error al obtener los datos.",
      });
    } finally {
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchDetalleVisitas()]);
      setLoading(false);
    };

    fetchData();
  }, []);


  const exportToExcel = async () => {
    try {
      if (dataCompleta.participantes.length === 0) {
        notification.warning({
          message: "Advertencia",
          description: "No hay datos disponibles para exportar.",
          duration: 3,
        });
        return;
      }

      const maximaVisita = dataCompleta.maximaVisita || 0;
      const maximaNovedad = dataCompleta.maximaNovedad || 0;

      setLoadingExcel(true);

      const workbook = new ExcelJS.Workbook();
      const templateResponse = await fetch(
        "/assets/plantilla_informe_general.xlsx"
      );
      const templateBuffer = await templateResponse.arrayBuffer();
      await workbook.xlsx.load(templateBuffer);
      const worksheet = workbook.getWorksheet(1);

      // Función para aplicar estilo a una celda
      const applyHeaderStyle = (cell, bgColor = "B4E4E8") => {
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: bgColor },
        };
        cell.font = {
          bold: true,
          name: "Arial",
        };
      };

      // Función para convertir número de columna a letra
      const columnNumberToLetter = (colNum) => {
        let letter = "";
        while (colNum > 0) {
          let remainder = (colNum - 1) % 26;
          letter = String.fromCharCode(65 + remainder) + letter;
          colNum = Math.floor((colNum - 1) / 26);
        }
        return letter;
      };

      let startCol = 45; // Columna AR (44)

      // 👉 Crear cabeceras dinámicas para VISITAS
      for (let i = 1; i <= maximaVisita; i++) {
        const colIndex = startCol + (i - 1) * 4; // ahora 4 columnas por visita
        const colLetraInicio = columnNumberToLetter(colIndex);
        const colLetraFin = columnNumberToLetter(colIndex + 3);

        // 1️⃣ Fila 1: VISITA 1, VISITA 2, etc. (fusiona 4 columnas)
        worksheet.mergeCells(`${colLetraInicio}1:${colLetraFin}1`);
        const titleCell = worksheet.getCell(`${colLetraInicio}1`);
        titleCell.value = `VISITA ${i}`;
        applyHeaderStyle(titleCell, "B4E4E8"); // Color celeste

        // 2️⃣ Fila 2:
        const stateCol = columnNumberToLetter(colIndex);
        const dateCol = columnNumberToLetter(colIndex + 1);
        const responsibleStartCol = columnNumberToLetter(colIndex + 2);
        const responsibleEndCol = columnNumberToLetter(colIndex + 3);

        // Combinar ESTADO verticalmente (fila 2 y 3)
        worksheet.mergeCells(`${stateCol}2:${stateCol}3`);
        const stateCell = worksheet.getCell(`${stateCol}2`);
        stateCell.value = "Estado";
        applyHeaderStyle(stateCell, "B4E4E8");

        // Combinar FECHA verticalmente (fila 2 y 3)
        worksheet.mergeCells(`${dateCol}2:${dateCol}3`);
        const dateCell = worksheet.getCell(`${dateCol}2`);
        dateCell.value = "Fecha";
        applyHeaderStyle(dateCell, "B4E4E8");

        // Combinar RESPONSABLE horizontalmente en fila 2 (2 columnas)
        worksheet.mergeCells(`${responsibleStartCol}2:${responsibleEndCol}2`);
        const responsibleCell = worksheet.getCell(`${responsibleStartCol}2`);
        responsibleCell.value = "Responsable";
        applyHeaderStyle(responsibleCell, "B4E4E8");

        // 3️⃣ Fila 3 (dividir debajo de RESPONSABLE)
        const emailCell = worksheet.getCell(`${responsibleStartCol}3`);
        emailCell.value = "Correo electrónico";
        applyHeaderStyle(emailCell, "B4E4E8");

        const nameCell = worksheet.getCell(`${responsibleEndCol}3`);
        nameCell.value = "Nombre";
        applyHeaderStyle(nameCell, "B4E4E8");
      }

      // 👉 Crear cabeceras dinámicas para NOVEDADES
      const startColNovedades = startCol + maximaVisita * 4; // ajustado a 4 por novedad
      for (let i = 1; i <= maximaNovedad; i++) {
        const colIndex = startColNovedades + (i - 1) * 5; // ahora usa 5 columnas por novedad (1 adicional para "EXISTE NOVEDAD")
        const colLetraInicio = columnNumberToLetter(colIndex);
        const colLetraFin = columnNumberToLetter(colIndex + 4); // 4 columnas por novedad ahora

        // 1️⃣ Fila 1: NOVEDAD 1, NOVEDAD 2, etc. (fusiona 5 columnas)
        worksheet.mergeCells(`${colLetraInicio}1:${colLetraFin}1`);
        const titleCell = worksheet.getCell(`${colLetraInicio}1`);
        titleCell.value = `NOVEDAD ${i}`;
        applyHeaderStyle(titleCell, "ADD8E6"); // Color azul claro

        // 2️⃣ Fila 2:
        const existsCol = columnNumberToLetter(colIndex); // Columna para "EXISTE NOVEDAD"
        const typeCol = columnNumberToLetter(colIndex + 1); // Columna para "TIPO DE NOVEDAD"
        const dateCol = columnNumberToLetter(colIndex + 2); // Columna para "FECHA"
        const responsibleStartCol = columnNumberToLetter(colIndex + 3); // Columna para "RESPONSABLE"
        const responsibleEndCol = columnNumberToLetter(colIndex + 4); // Columna para "RESPONSABLE" (fin)

        // Combinar EXISTE NOVEDAD verticalmente (fila 2 y 3)
        worksheet.mergeCells(`${existsCol}2:${existsCol}3`);
        const existsCell = worksheet.getCell(`${existsCol}2`);
        existsCell.value = "Existe novedad";
        applyHeaderStyle(existsCell, "ADD8E6");

        // Combinar TIPO DE NOVEDAD verticalmente (fila 2 y 3)
        worksheet.mergeCells(`${typeCol}2:${typeCol}3`);
        const typeCell = worksheet.getCell(`${typeCol}2`);
        typeCell.value = "Tipo de novedad";
        applyHeaderStyle(typeCell, "ADD8E6");

        // Combinar FECHA verticalmente (fila 2 y 3)
        worksheet.mergeCells(`${dateCol}2:${dateCol}3`);
        const dateCell = worksheet.getCell(`${dateCol}2`);
        dateCell.value = "Fecha";
        applyHeaderStyle(dateCell, "ADD8E6");

        // Combinar RESPONSABLE horizontalmente en fila 2 (2 columnas)
        worksheet.mergeCells(`${responsibleStartCol}2:${responsibleEndCol}2`);
        const responsibleCell = worksheet.getCell(`${responsibleStartCol}2`);
        responsibleCell.value = "Responsable";
        applyHeaderStyle(responsibleCell, "ADD8E6");

        // 3️⃣ Fila 3 (dividir debajo de RESPONSABLE)
        const emailCell = worksheet.getCell(`${responsibleStartCol}3`);
        emailCell.value = "Correo electrónico";
        applyHeaderStyle(emailCell, "ADD8E6");

        const nameCell = worksheet.getCell(`${responsibleEndCol}3`);
        nameCell.value = "Nombre";
        applyHeaderStyle(nameCell, "ADD8E6");
      }

      let autorizacionesKeys = [
        "uso_nombre",
        "uso_fotos_videos",
        "uso_frases",
        "uso_declaraciones",
        "uso_archivo_audiovisual",
        "uso_otro",
        "uso_entrevista",
        "uso_imagen",
      ];
      // 2️⃣ Agregar datos
      let currentRow = 4;

      dataCompleta.participantes.forEach((item) => {
        const sectionRowStart = currentRow;

        // Formulario y autorizaciones (desde la columna N)
        const formularios = item.detalleFormulariosPatti || {};
        const visitas = item.listaVisitasTecnicasFinal?.visitas || [];

        const novedades = item.listaNovedades || [];

        // Datos básicos
        worksheet.getCell(`A${currentRow}`).value = item.codigo_sir || "";
        worksheet.getCell(`B${currentRow}`).value = item.nombre_if || "";
        worksheet.getCell(`C${currentRow}`).value = item.identificacion || "NA";
        worksheet.getCell(`D${currentRow}`).value = item.telefono || "NA";
        worksheet.getCell(`E${currentRow}`).value = item.departamento || "";
        worksheet.getCell(`F${currentRow}`).value = 
        item.codigoDaneDepartamento && Number(item.codigoDaneDepartamento) >= 1 && Number(item.codigoDaneDepartamento) <= 9
          ? "0" + item.codigoDaneDepartamento
          : item.codigoDaneDepartamento || "";
      
        worksheet.getCell(`G${currentRow}`).value = item.municipio || "";
          worksheet.getCell(`H${currentRow}`).value = 
  item.codigoDaneMunicipio && Number(item.codigoDaneMunicipio) >= 1 && Number(item.codigoDaneMunicipio) <= 9
    ? "0" + item.codigoDaneMunicipio
    : item.codigoDaneMunicipio || "";

        worksheet.getCell(`I${currentRow}`).value =
          item.grupo_territorial || "";
        worksheet.getCell(`J${currentRow}`).value = item.codigo_pre || "";
        worksheet.getCell(`K${currentRow}`).value = item.nombre_proyecto || "";
        worksheet.getCell(`L${currentRow}`).value = item.sexo_sir || "";
        worksheet.getCell(`M${currentRow}`).value = item.grupo_etario || "";
        worksheet.getCell(`N${currentRow}`).value = 
        item.sector === "Pecuaria" ? "Pecuario" : (item.sector || "");
      
        worksheet.getCell(`O${currentRow}`).value =
          item.actividad_productiva || "";
        worksheet.getCell(`P${currentRow}`).value = item.estado || "";

        let currentCol = 17; // Columna P

        for (let i = 1; i <= 5; i++) {
          const formulario = formularios[`formulario_${i}`] || {};

          // Escribe estado, fecha y responsable_email, responsable_name
          worksheet.getCell(currentRow, currentCol).value =
            formulario.estado || "";
          worksheet.getCell(currentRow, currentCol + 1).value =
            formulario.fecha || "";
          worksheet.getCell(currentRow, currentCol + 2).value =
            formulario.email_responsable || "";
          worksheet.getCell(currentRow, currentCol + 3).value =
            formulario.name_responsable || "";
          currentCol += 4;

          // Si es el formulario 4, agregamos autorizaciones
          if (i === 4 && Array.isArray(formulario.autorizaciones)) {
            autorizacionesKeys.forEach((aut) => {
              const tiene = formulario.autorizaciones.includes(aut);
              worksheet.getCell(currentRow, currentCol).value = tiene
                ? "SI"
                : "NO";
              currentCol++;
            });
          }
        }

        // Visitas (a partir de la columna AM)
        for (let i = 0; i < maximaVisita; i++) {
          const colIndex = 45 + i * 4;
          const visita = visitas[i]; // puede ser undefined si no hay visita

          worksheet.getCell(currentRow, colIndex).value = visita
            ? "realizada"
            : "";
          worksheet.getCell(currentRow, colIndex + 1).value =
            visita?.fecha || "";
          worksheet.getCell(currentRow, colIndex + 2).value =
            visita?.email_responsable || "";

          worksheet.getCell(currentRow, colIndex + 3).value =
            visita?.name_responsable || "";
        }
        // Novedades (a partir de la columna AK)

        for (let i = 0; i < maximaNovedad; i++) {
          const colIndex = startColNovedades + i * 5;
          const novedad = novedades[i];

          // Verificar si la novedad es un array vacío
          const isEmpty = !novedad || novedad.length === 0;

          worksheet.getCell(currentRow, colIndex).value = isEmpty ? "NO" : "SI";
          worksheet.getCell(currentRow, colIndex + 1).value =
            novedad?.tipo_novedad || "";
          worksheet.getCell(currentRow, colIndex + 2).value =
            novedad?.fecha || "";
          worksheet.getCell(currentRow, colIndex + 3).value =
            novedad?.email_responsable || "";

          worksheet.getCell(currentRow, colIndex + 4).value =
            novedad?.name_responsable || "";
        }

        currentRow++;
      });

      // 3️⃣ Exportar
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, "Informe_general.xlsx");

      notification.success({
        message: "Éxito",
        description: "Excel exportado correctamente.",
        duration: 3,
      });
    } catch (error) {
      console.error("Error al generar Excel:", error);
      notification.error({
        message: "Error",
        description: "No se pudo exportar el Excel.",
        duration: 3,
      });
    } finally {
      setLoadingExcel(false);
    }
  };

  const exportToJSON = async () => {
    try {
      if (
        !dataCompleta.participantes ||
        dataCompleta.participantes.length === 0
      ) {
        notification.warning({
          message: "Advertencia",
          description: "No hay datos disponibles para exportar.",
          duration: 3,
        });
        return;
      }

      setLoadingJson(true);
      // Convertir data a JSON y descargar
      const jsonBlob = new Blob(
        [JSON.stringify(dataCompleta.participantes, null, 2)],
        {
          type: "application/json",
        }
      );
      saveAs(jsonBlob, "Informe_general.json");

      notification.success({
        message: "Éxito",
        description: "JSON exportado correctamente.",
        duration: 3,
      });
    } catch (error) {
      console.error("Error al generar JSON:", error);
      notification.error({
        message: "Error",
        description: "No se pudo exportar el JSON.",
        duration: 3,
      });
    } finally {
      setLoadingJson(false);
    }
  };

  return (
    <Space>
      <Tooltip title="Exportar datos en XLSX">
        <Button
          type="default"
          icon={<FileExcelOutlined />}
          onClick={exportToExcel}
          loading={loading || loadingExcel}
          disabled={loading || loadingExcel}
          style={{
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
            color: "white",
          }}
        />
      </Tooltip>

      <Tooltip title="Exportar datos en JSON">
        <Button
          type="default"
          icon={<FileTextOutlined />}
          onClick={exportToJSON}
          loading={loading || loadingJson}
          disabled={loading || loadingJson}
          style={{
            backgroundColor: "#C2BCBC",
            borderColor: "#C2BCBC",
            color: "white",
          }}
        />
      </Tooltip>
    </Space>
  );
};

export default ExportInformeGeneral;
